import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Sidebar        from '../../component/DashboardUI/Sidebar';
import Topbar         from '../../component/DashboardUI/Topbar';
import { Badge, StatCard, Panel, ActivityList, ProgressBar, TrackCell, statusBadge } from '../../component/DashboardUI/UI';
import { ThemeProvider } from '../../hooks/useTheme';
import { CONTESTANT, CONTESTANT_NAV, ACTIVITY_CONTESTANT, TIMELINE } from '../../data/mockData';
import '../../asset/css/main.css';

const SCORE_VALS  = [22, 24, 20, 21];
const SCORE_CRITS = ['Melody & Composition', 'Production Quality', 'Originality', 'Emotional Impact'];

export default function ContestantDashboard() {
  return (
    <ThemeProvider>
      <PageContent />
    </ThemeProvider>
  );
}

function PageContent() {
  const [sideOpen, setSideOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="page-shell">
      {/* ── Sidebar ── */}
      <Sidebar
        navLinks={CONTESTANT_NAV}
        user={CONTESTANT}
        isJudge={false}
        isOpen={sideOpen}
        onClose={() => setSideOpen(false)}
      />

      {/* ── Right body ── */}
      <div className="page-body">
        {/* ── Topbar ── */}
        <Topbar title="Artist Dashboard" onMenuClick={() => setSideOpen(o => !o)} />

        {/* ── Page content ── */}
        <main className="page-content anim-up">

          {/* Stat row */}
          <div className="stat-row">
            <StatCard icon="🎵" cls="red"    val="3"    label="Tracks Submitted" delta="↑ 1 this week"   up />
            <StatCard icon="⭐" cls="gold"   val="80"   label="Avg Score"        delta="↑ 6 pts vs last" up />
            <StatCard icon="👁" cls="green"  val="1.2K" label="Track Plays"      delta="↑ 340 this week" up />
            <StatCard icon="🏆" cls="purple" val="#14"  label="Leaderboard Rank" delta="↑ 3 positions"   up />
          </div>

          {/* CTA banner */}
          <div style={{
            background: 'linear-gradient(135deg, var(--red-dim), var(--purple-dim))',
            border: '1px solid var(--red-glow)', borderRadius: 12,
            padding: '1.4rem 1.7rem', marginBottom: '1.7rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '1rem', flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.35rem', letterSpacing: '0.05em', marginBottom: 4, color: 'var(--text)' }}>
                Season 4 Submissions Close Apr 30
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--muted2)' }}>
                Upload your best track before the deadline — judging starts May 1.
              </div>
            </div>
            <button className="btn btn-red" onClick={() => navigate('/contestant/upload')}>
              ⬆ Upload Now
            </button>
          </div>

          {/* Two-col layout */}
          <div className="row">
            {/* Left */}
            <div className="col-grow">
              {/* Tracks table */}
              <div>
                <div className="sec-head">
                  <h3>My Tracks</h3>
                  <button className="btn btn-red btn-sm" onClick={() => navigate('/contestant/upload')}>+ Upload</button>
                </div>
                <div className="tbl-wrap">
                  <table>
                    <thead><tr><th>Track</th><th>Status</th><th>Score</th><th>Date</th></tr></thead>
                    <tbody>
                      {CONTESTANT.tracks.map(t => (
                        <tr key={t.id}>
                          <td><TrackCell img={t.img} title={t.title} sub={t.genre} /></td>
                          <td>{statusBadge(t.status)}</td>
                          <td>
                            {t.score
                              ? <span style={{ fontFamily: "'Bebas Neue',cursive", fontSize: '1.2rem', color: t.score >= 80 ? 'var(--green)' : t.score >= 60 ? 'var(--gold)' : 'var(--red)' }}>{t.score}</span>
                              : <span style={{ color: 'var(--muted)' }}>—</span>
                            }
                          </td>
                          <td style={{ whiteSpace: 'nowrap', color: 'var(--muted)' }}>{t.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Score breakdown */}
              <Panel title="Score Breakdown — Midnight Pulse (87)">
                {SCORE_CRITS.map((c, i) => (
                  <div key={c} style={{ marginBottom: '0.85rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.77rem', marginBottom: 5 }}>
                      <span style={{ color: 'var(--muted2)', fontWeight: 600 }}>{c}</span>
                      <span style={{ fontFamily: 'Space Mono,monospace', fontSize: '0.7rem', color: 'var(--gold)' }}>{SCORE_VALS[i]}/25</span>
                    </div>
                    <ProgressBar pct={(SCORE_VALS[i] / 25) * 100} color="var(--gold)" />
                  </div>
                ))}
              </Panel>
            </div>

            {/* Right */}
            <div className="col-side">
              <Panel title="Recent Activity">
                <ActivityList items={ACTIVITY_CONTESTANT} />
              </Panel>

              {/* Timeline */}
              <Panel title="Competition Timeline">
                {TIMELINE.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: i < TIMELINE.length - 1 ? '0.85rem' : 0, opacity: item.done || item.active ? 1 : 0.4 }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, background: item.done ? 'var(--green)' : item.active ? 'var(--red)' : 'var(--bg3)', border: `2px solid ${item.done ? 'var(--green)' : item.active ? 'var(--red)' : 'var(--border2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', color: item.done ? '#000' : '#fff' }}>
                      {item.done ? '✓' : item.active ? '●' : ''}
                    </div>
                    <div style={{ flex: 1, fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)' }}>{item.label}</div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontFamily: 'Space Mono,monospace', whiteSpace: 'nowrap' }}>{item.date}</div>
                  </div>
                ))}
              </Panel>

              {/* Rank */}
              <Panel style={{ background: 'var(--gold-dim)', border: '1px solid rgba(255,183,3,0.2)' }}>
                <div style={{ fontFamily: "'Bebas Neue',cursive", fontSize: '1rem', color: 'var(--gold)', marginBottom: 4 }}>Current Rank</div>
                <div style={{ fontFamily: "'Bebas Neue',cursive", fontSize: '3rem', lineHeight: 1, color: 'var(--text)' }}>#14</div>
                <div style={{ fontSize: '0.77rem', color: 'var(--muted2)', marginTop: 4 }}>Electronic category · Top 20%</div>
                <div style={{ marginTop: '0.85rem' }}>
                  <ProgressBar pct={80} color="var(--gold)" />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.67rem', color: 'var(--muted)', marginTop: 4 }}>
                    <span>Rank 14</span><span>Top 10 → Finals</span>
                  </div>
                </div>
              </Panel>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}